/** お知らせ(suite 共通)の画面部品。ヘッダーの鈴と一覧で同じ行の見た目を使う。
 *
 * 種別の日本語は core が持つ(kind_label をそのまま出す。画面は対応表を持たない)。
 */
import { BellOutlined, CheckOutlined, DeleteOutlined } from "@ant-design/icons";
import { Badge, Button, Empty, List, Popconfirm, Space, Tag, Typography } from "antd";
import { useNavigate } from "react-router-dom";

import type { NotificationRow } from "../lib/api";
import { jst } from "../lib/format";
import { useMe } from "../lib/useApi";
import { useDeleteNotification, useDeleteReadNotifications, useMarkRead } from "./notificationHooks";

const { Text } = Typography;

export function UnreadBell({ unread }: { unread: number }) {
  return (
    <Badge count={unread} size="small" overflowCount={99} data-testid="notif-badge">
      <BellOutlined className="ux-bell" />
    </Badge>
  );
}

/** 「既読を消す」。一覧の上(ドロワーとお知らせ画面)に置く。未読は残る。
 *  全体宛は 1 行を全員で共有しているので、suite:admin 以外では残る(HIDE 2026-10-20)。 */
export function DeleteReadButton({ disabled }: { disabled?: boolean }) {
  const del = useDeleteReadNotifications();
  const me = useMe();
  const admin = me.data?.is_suite_admin === true;
  return (
    <Popconfirm
      title="既読のお知らせをすべて消す"
      description={admin ? "未読は残る。全体宛も消える(全員から消える)。消したお知らせは戻せない。" : "未読と全体宛は残る。消したお知らせは戻せない。"}
      okText="消す"
      okButtonProps={{ danger: true }}
      cancelText="やめる"
      onConfirm={() => del.mutate()}
      disabled={disabled}
    >
      <Button size="small" icon={<DeleteOutlined />} loading={del.isPending} disabled={disabled}
        data-testid="notif-delete-read">
        既読を消す
      </Button>
    </Popconfirm>
  );
}

/** 1件の行。link があれば押すとその画面へ移り、そのとき既読にする。既読の行は消せる
 *  (全体宛は全員から消えるので suite:admin だけ。他の人には「消す」を出さない)。 */
export function NotificationItem({
  row,
  onNavigate,
}: {
  row: NotificationRow;
  onNavigate?: () => void;
}) {
  const navigate = useNavigate();
  const markRead = useMarkRead();
  const del = useDeleteNotification();
  const me = useMe();
  const unread = row.read_at === null;
  const canDelete = row.user_sub !== null || me.data?.is_suite_admin === true;

  const open = () => {
    if (unread) markRead.mutate(row.id);
    if (row.link) {
      onNavigate?.();
      navigate(row.link);
    }
  };

  return (
    <List.Item
      data-testid="notif-row"
      style={{ alignItems: "flex-start", cursor: row.link ? "pointer" : "default" }}
      onClick={row.link ? open : undefined}
      actions={
        unread
          ? [
              <Button
                key="read"
                size="small"
                type="text"
                icon={<CheckOutlined />}
                loading={markRead.isPending}
                onClick={(e) => {
                  e.stopPropagation();
                  markRead.mutate(row.id);
                }}
              >
                既読にする
              </Button>,
            ]
          : canDelete
            ? [
              <Button
                key="delete"
                size="small"
                type="text"
                icon={<DeleteOutlined />}
                loading={del.isPending}
                onClick={(e) => {
                  e.stopPropagation();
                  del.mutate(row.id);
                }}
              >
                消す
              </Button>,
            ]
            : []
      }
    >
      <List.Item.Meta
        title={
          <Space size={6} wrap>
            {unread && <Badge status="processing" />}
            <Tag>{row.kind_label}</Tag>
            <Text strong={unread}>{row.title}</Text>
          </Space>
        }
        description={
          <Space direction="vertical" size={2}>
            {row.body && <Text type="secondary">{row.body}</Text>}
            <Text type="secondary" style={{ fontSize: 12 }}>
              {jst(row.created_at)}
              {row.read_at ? "(既読)" : ""}
            </Text>
          </Space>
        }
      />
    </List.Item>
  );
}

export function NotificationList({
  rows,
  loading,
  onNavigate,
}: {
  rows: NotificationRow[];
  loading?: boolean;
  onNavigate?: () => void;
}) {
  if (!loading && rows.length === 0) {
    return <Empty description="お知らせは無い" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }
  return (
    <List
      size="small"
      loading={loading}
      dataSource={rows}
      renderItem={(row) => (
        <NotificationItem key={row.id} row={row} onNavigate={onNavigate} />
      )}
    />
  );
}
